import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * TextReveal — splits text into words and reveals them on scroll.
 * Honors prefers-reduced-motion (renders flat).
 */
export default function TextReveal({
    children,
    as: Tag = "p",
    className = "",
    stagger = 0.045,
    duration = 0.7,
    y = 18,
    ...rest
}) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) {
            el.querySelectorAll("[data-reveal-word]").forEach((w) => {
                w.style.opacity = "1";
                w.style.transform = "translateY(0)";
            });
            return;
        }

        const words = el.querySelectorAll("[data-reveal-word]");
        const ctx = gsap.context(() => {
            gsap.fromTo(
                words,
                { opacity: 0, y, filter: "blur(6px)" },
                {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    duration,
                    stagger,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: el,
                        start: "top 78%",
                        end: "bottom 50%",
                        toggleActions: "play none none reverse",
                    },
                }
            );
        }, el);

        return () => ctx.revert();
    }, [stagger, duration, y]);

    const text = typeof children === "string" ? children : "";
    const words = text.split(" ");

    return (
        <Tag ref={ref} className={className} {...rest}>
            {words.map((w, i) => (
                <span
                    key={i}
                    data-reveal-word
                    style={{ display: "inline-block", opacity: 0, willChange: "opacity, transform" }}
                >
                    {w}
                    {i < words.length - 1 && "\u00A0"}
                </span>
            ))}
        </Tag>
    );
}
